import React, { useEffect, useRef, useState } from 'react';
import Textfield from '@atlaskit/textfield';
import styled from '@emotion/styled';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import { gridSize } from '@atlaskit/theme';

export const OTP_CODE_CHARACTERS = [
  ' ',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
] as const;
/**
 * The space character (' ') represents the absence of a character.
 * This is to allow character positions to be preserved,
 * @example 1 2 3 4 5 6
 * @example _ 5 _ _ 0 9
 */
export type OtpCodeCharacter = (typeof OTP_CODE_CHARACTERS)[number];
type OtpCodeArray = OtpCodeCharacter[];

export interface OtpInputProps {
  /** Callback to be called when the OTP value changes */
  onChange: (otp: string) => void;
  /** Value of the OTP code input */
  otp: string;
  /** Full length of otp code input */
  otpCodeFullLength: number;
  /** Disables the input textfields when loading */
  isLoading: boolean;
  /** Whether the OTP input should be in error state */
  isError: boolean;
}

const InputWrapper = styled.div`
  display: flex;
  gap: ${gridSize()}px;

  input {
    text-align: center;
    font-size: 20px;
  }
`;

const OtpInput = ({ otp, otpCodeFullLength, onChange, isLoading, isError }: OtpInputProps) => {
  const [focusedInputIndex, setFocusedInputIndex] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const otpCodeArray: OtpCodeCharacter[] = parseOtp(otp);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, otpCodeFullLength);
  }, [otpCodeFullLength]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const isInputValueValid = (value: string): value is OtpCodeCharacter =>
    value.trim().length === 1 && isOtpCodeCharacter(value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();

    if (isInputValueValid(value)) {
      changeOtpCodeAtFocus(value);
      focusInput(focusedInputIndex + 1);
    } else {
      const { nativeEvent } = event;
      // @ts-expect-error - This was added previosly to handle and edge case
      // for dealing with keyCode "229 Unidentified" on Android. Check if this is
      // still needed.
      if (nativeEvent.data === null && nativeEvent.inputType === 'deleteContentBackward') {
        event.preventDefault();
        changeOtpCodeAtFocus(' ');
        focusInput(focusedInputIndex - 1);
      }
    }
  };

  const handleFocus = (index: number) => (event: React.FocusEvent<HTMLInputElement>) => {
    focusInput(index);
    event.target.select();
  };

  const handleBlur = () => {
    setFocusedInputIndex(focusedInputIndex - 1);
  };

  const handleBackspace = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { selectionStart, selectionEnd } = event.target as HTMLInputElement;
    if (selectionStart === 0 && selectionEnd === 0) {
      if (focusedInputIndex > 0) {
        const newFocusedIndex = focusedInputIndex - 1;
        focusInput(newFocusedIndex);
        changeOtpCodeAtIndex(newFocusedIndex, ' ');
      }
    } else {
      changeOtpCodeAtFocus(' ');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Delete':
        event.preventDefault();
        changeOtpCodeAtFocus(' ');
        break;
      case 'Backspace':
        handleBackspace(event);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        focusInput(focusedInputIndex - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        focusInput(focusedInputIndex + 1);
        break;
      case 'Space':
      case 'Spacebar':
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        break;
      default:
        // React does not trigger onChange when the same value is entered
        // again. So we need to focus the next input manually in this case.
        if (event.key === otpCodeArray[focusedInputIndex]) {
          event.preventDefault();
          focusInput(focusedInputIndex + 1);
        }
    }
  };

  const focusInput = (index: number) => {
    const indexToFocusInput = Math.max(Math.min(otpCodeFullLength - 1, index), 0);

    inputRefs.current[indexToFocusInput]?.focus();
    inputRefs.current[indexToFocusInput]?.select();
    setFocusedInputIndex(indexToFocusInput);
  };

  const changeOtpCodeAtIndex = (index: number, value: OtpCodeCharacter) => {
    otpCodeArray[index] = value;
    onChange(otpCodeArrayToString(otpCodeArray));
  };

  const changeOtpCodeAtFocus = (value: OtpCodeCharacter) =>
    changeOtpCodeAtIndex(focusedInputIndex, value);

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    // Get dataToPaste in an array of max size (num of inputs - current position)
    const dataToPaste = event.clipboardData
      .getData('text/plain')
      .slice(0, otpCodeFullLength - focusedInputIndex)
      .toUpperCase()
      .split('');

    // Prevent pasting if the clipboard data contains non-alphanumeric values for number inputs
    if (dataToPaste.some(value => !isOtpCodeCharacter(value))) {
      return;
    }

    // Paste data from focused input onwards
    let indexToPaste = focusedInputIndex;
    while (indexToPaste < otpCodeFullLength && dataToPaste.length > 0) {
      otpCodeArray[indexToPaste] = (dataToPaste as OtpCodeArray).shift()!;
      indexToPaste++;
    }

    focusInput(indexToPaste);
    onChange(otpCodeArrayToString(otpCodeArray));
  };

  return (
    <InputWrapper>
      {otpCodeArray.map((otpCodeDigitValue, index) => (
        <div key={`otp-div-index-${index}`}>
          <Textfield
            isDisabled={isLoading}
            isInvalid={isError}
            value={otpCodeDigitValue === ' ' ? '' : otpCodeDigitValue}
            ref={(element: HTMLInputElement) => (inputRefs.current[index] = element)}
            onChange={handleChange}
            onFocus={handleFocus(index)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            autoComplete="off"
            maxLength={1}
            aria-label={`Please enter OTP character ${index + 1}`}
            type="text"
            testId={`otp-input-index-${index}`}
          />
        </div>
      ))}
    </InputWrapper>
  );
};

const isOtpCodeCharacter = (c: any): c is OtpCodeCharacter => OTP_CODE_CHARACTERS.includes(c);

const parseOtp = (otp: string): OtpCodeCharacter[] => {
  const otpCodeArray = otp.split('').filter(isOtpCodeCharacter);
  if (otp.length !== otpCodeArray.length) {
    captureErrorMessage('Error parsing OTP - received invalid OTP character', { otp });
  }
  return otpCodeArray;
};

const otpCodeArrayToString = (otpCodeArray: OtpCodeArray): string => otpCodeArray.join('');

export default OtpInput;
